import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaEnvelope,
  FaSearch,
  FaSyncAlt,
  FaBan,
  FaCalendarAlt,
  FaGlobe,
} from "react-icons/fa";
import {
  getAllDesinscriptions,
  deleteDesinscriptionById,
} from "./../../../../services/desinscriptionServices";
import ButtonDelete from "../../../../components/Button/ButtonDelete";
import { ToastContainer, toast } from "react-toastify";
import useScrollToTop from "./../../../../hooks/useScrollToTop";

const CARD =
  "rounded-[26px] border border-slate-200/80 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 shadow-[0_18px_50px_-20px_rgba(15,23,42,0.28)] backdrop-blur";
const HERO =
  "relative overflow-hidden rounded-[30px] bg-slate-950 text-white border border-slate-800 shadow-[0_25px_70px_-25px_rgba(15,23,42,0.85)]";
const INPUT =
  "w-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 text-sm text-slate-800 dark:text-slate-100 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-900/40";
const BUTTON_SOFT =
  "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-medium transition";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("fr-FR");
}

function getEmailDomain(email = "") {
  const parts = String(email).split("@");
  return parts[1] || "—";
}

function isRecent(dateStr, days = 7) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  const diff = now - d;
  return diff <= days * 24 * 60 * 60 * 1000;
}

function StatCard({ icon, label, value, hint, dark = false }) {
  return (
    <div
      className={
        dark
          ? "rounded-[22px] border border-white/15 bg-white/10 p-4 backdrop-blur-md"
          : "rounded-[22px] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-sm"
      }
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div
            className={`text-xs uppercase tracking-[0.18em] ${
              dark ? "text-white/60" : "text-slate-500 dark:text-slate-400"
            }`}
          >
            {label}
          </div>
          <div
            className={`mt-2 text-2xl font-semibold ${
              dark ? "text-white" : "text-slate-900 dark:text-slate-50"
            }`}
          >
            {value}
          </div>
          {hint ? (
            <div
              className={`mt-1 text-xs ${
                dark ? "text-white/70" : "text-slate-500 dark:text-slate-400"
              }`}
            >
              {hint}
            </div>
          ) : null}
        </div>

        <div
          className={`rounded-2xl p-3 ${
            dark
              ? "bg-white/10 text-white"
              : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
          }`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function SectionHead({ eyebrow, title, description, action }) {
  return (
    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        {eyebrow ? (
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-500">
            {eyebrow}
          </div>
        ) : null}
        <h2 className="mt-1 text-xl font-semibold text-slate-900 dark:text-slate-50">
          {title}
        </h2>
        {description ? (
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {description}
          </p>
        ) : null}
      </div>
      {action ? <div>{action}</div> : null}
    </div>
  );
}

function Badge({ children, tone = "gray" }) {
  const tones = {
    gray: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
    red: "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
    blue: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",
    purple:
      "bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300",
  };

  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${tones[tone] || tones.gray}`}>
      {children}
    </span>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 ${className}`}
    >
      {children}
    </th>
  );
}

function Td({ children, className = "" }) {
  return (
    <td className={`px-4 py-4 align-top text-sm text-slate-700 dark:text-slate-200 ${className}`}>
      {children}
    </td>
  );
}

function EmptyState() {
  return (
    <div className="p-10 text-center">
      <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-2xl dark:bg-slate-800">
        🚫
      </div>
      <div className="text-lg font-semibold text-slate-900 dark:text-slate-50">
        Aucune désinscription trouvée
      </div>
      <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        Il n’y a aucun résultat pour les filtres actuels.
      </div>
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="p-5 space-y-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 animate-pulse"
        />
      ))}
    </div>
  );
}

function MobileInfo({ label, value }) {
  return (
    <div className="rounded-2xl bg-white dark:bg-slate-900 p-3">
      <div className="text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div className="mt-1 font-medium text-slate-900 dark:text-slate-50">
        {value}
      </div>
    </div>
  );
}

const DesinscriptionsPage = () => {
  useScrollToTop();

  const [desinscriptions, setDesinscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchDesinscriptions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAllDesinscriptions();
      const rows = Array.isArray(data) ? data : [];

      setDesinscriptions(
        rows.sort(
          (a, b) =>
            new Date(b.date_desinscription) - new Date(a.date_desinscription)
        )
      );
    } catch (error) {
      console.error("Erreur lors du chargement des désinscriptions:", error);
      toast.error("Erreur lors du chargement des désinscriptions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDesinscriptions();
  }, [fetchDesinscriptions]);

  const filteredDesinscriptions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return desinscriptions.filter((item) => {
      if (!term) return true;

      const email = String(item.email || "").toLowerCase();
      const domain = getEmailDomain(item.email).toLowerCase();
      const date = formatDate(item.date_desinscription).toLowerCase();

      return (
        email.includes(term) ||
        domain.includes(term) ||
        date.includes(term)
      );
    });
  }, [desinscriptions, searchTerm]);

  const stats = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    let recent7 = 0;
    let currentMonthCount = 0;
    const uniqueDomains = new Set();

    filteredDesinscriptions.forEach((item) => {
      if (isRecent(item.date_desinscription, 7)) recent7 += 1;

      const d = new Date(item.date_desinscription);
      if (
        !Number.isNaN(d.getTime()) &&
        d.getMonth() === currentMonth &&
        d.getFullYear() === currentYear
      ) {
        currentMonthCount += 1;
      }

      uniqueDomains.add(getEmailDomain(item.email).toLowerCase());
    });

    return {
      total: filteredDesinscriptions.length,
      recent7,
      currentMonthCount,
      domains: uniqueDomains.size,
    };
  }, [filteredDesinscriptions]);

  const deleteDesinscription = async (desinscriptionId) => {
    if (
      !window.confirm(
        "Êtes-vous sûr de vouloir supprimer cette désinscription ?"
      )
    ) {
      return;
    }

    try {
      await deleteDesinscriptionById(desinscriptionId);
      toast.success("Désinscription supprimée avec succès");
      setDesinscriptions((prev) =>
        prev.filter((d) => d.id !== desinscriptionId)
      );
    } catch (error) {
      console.error(
        "Erreur lors de la suppression de la désinscription:",
        error
      );
      toast.error("Erreur lors de la suppression de la désinscription");
    }
  };

  const isEmpty = !loading && filteredDesinscriptions.length === 0;

  return (
    <div className="space-y-6">
      {/* HERO */}
      <section className={HERO}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(129,140,248,0.35),transparent_30%),radial-gradient(circle_at_top_right,rgba(236,72,153,0.30),transparent_25%),radial-gradient(circle_at_bottom_left,rgba(34,197,94,0.16),transparent_30%)]" />
        <div className="relative p-6 md:p-8">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-white/80">
                <FaBan />
                Notifications & e-mails
              </div>

              <h1 className="mt-4 text-3xl font-semibold tracking-tight md:text-4xl">
                Gestion des désinscriptions
              </h1>

              <p className="mt-3 max-w-2xl text-sm leading-6 text-white/75 md:text-base">
                Consulte les utilisateurs désinscrits de tes communications,
                retrouve rapidement un e-mail et garde une vue claire sur
                l’évolution des désinscriptions.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={fetchDesinscriptions}
                className={`${BUTTON_SOFT} bg-white/10 text-white hover:bg-white/20 border border-white/15`}
              >
                <FaSyncAlt />
                Rafraîchir
              </button>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <StatCard
              dark
              icon={<FaEnvelope />}
              label="Désinscriptions"
              value={stats.total}
              hint="Résultats affichés"
            />
            <StatCard
              dark
              icon={<FaCalendarAlt />}
              label="7 derniers jours"
              value={stats.recent7}
              hint="Désinscriptions récentes"
            />
            <StatCard
              dark
              icon={<FaCalendarAlt />}
              label="Ce mois-ci"
              value={stats.currentMonthCount}
              hint="Depuis le début du mois"
            />
            <StatCard
              dark
              icon={<FaGlobe />}
              label="Domaines e-mail"
              value={stats.domains}
              hint="Diversité des domaines"
            />
          </div>
        </div>
      </section>

      {/* FILTRE */}
      <section className={`${CARD} p-5 md:p-6`}>
        <SectionHead
          eyebrow="Recherche"
          title="Retrouver rapidement une adresse"
          description="Recherche par e-mail, domaine ou date de désinscription."
        />

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="relative md:col-span-2">
            <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className={`${INPUT} pl-11`}
              type="text"
              placeholder="Rechercher un e-mail, un domaine, une date…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex items-center justify-between rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
            <span>{filteredDesinscriptions.length} résultat(s)</span>
            {searchTerm ? <Badge tone="blue">Filtré</Badge> : <Badge>Global</Badge>}
          </div>
        </div>
      </section>

      {/* TABLE / CARDS */}
      <section className={`${CARD} overflow-hidden`}>
        <div className="border-b border-slate-200 dark:border-slate-800 px-5 py-4 md:px-6">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                Liste des désinscriptions
              </h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Historique des utilisateurs ayant quitté la liste de diffusion.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="red">{filteredDesinscriptions.length} affichée(s)</Badge>
            </div>
          </div>
        </div>

        {loading ? (
          <SkeletonList />
        ) : isEmpty ? (
          <EmptyState />
        ) : (
          <>
            {/* desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-[950px] w-full">
                <thead className="bg-slate-50 dark:bg-slate-800/60">
                  <tr>
                    <Th>Email</Th>
                    <Th>Domaine</Th>
                    <Th>Date de désinscription</Th>
                    <Th>Statut</Th>
                    <Th className="text-right">Actions</Th>
                  </tr>
                </thead>

                <tbody>
                  {filteredDesinscriptions.map((desinscription) => (
                    <tr
                      key={desinscription.id}
                      className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/70 dark:hover:bg-slate-800/30 transition"
                    >
                      <Td className="min-w-[260px]">
                        <div className="font-semibold text-slate-900 dark:text-slate-50">
                          {desinscription.email}
                        </div>
                      </Td>

                      <Td className="min-w-[180px]">
                        <Badge tone="purple">
                          {getEmailDomain(desinscription.email)}
                        </Badge>
                      </Td>

                      <Td className="min-w-[220px]">
                        {formatDate(desinscription.date_desinscription)}
                      </Td>

                      <Td className="min-w-[130px]">
                        {isRecent(desinscription.date_desinscription, 7) ? (
                          <Badge tone="blue">Récent</Badge>
                        ) : (
                          <Badge tone="gray">Archivé</Badge>
                        )}
                      </Td>

                      <Td className="min-w-[110px]">
                        <div className="flex justify-end gap-2">
                          <ButtonDelete
                            onClick={() =>
                              deleteDesinscription(desinscription.id)
                            }
                            hoverLabel="Supprimer"
                          />
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* mobile */}
            <div className="grid gap-4 p-4 md:hidden">
              {filteredDesinscriptions.map((desinscription) => (
                <div
                  key={desinscription.id}
                  className="rounded-[24px] border border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-800/40 p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 dark:text-slate-50 break-all">
                        {desinscription.email}
                      </div>
                      <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        {getEmailDomain(desinscription.email)}
                      </div>
                    </div>

                    {isRecent(desinscription.date_desinscription, 7) ? (
                      <Badge tone="blue">Récent</Badge>
                    ) : (
                      <Badge tone="gray">Archivé</Badge>
                    )}
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3">
                    <MobileInfo
                      label="Date de désinscription"
                      value={formatDate(desinscription.date_desinscription)}
                    />
                  </div>

                  <div className="mt-4 flex gap-2">
                    <ButtonDelete
                      onClick={() => deleteDesinscription(desinscription.id)}
                      hoverLabel="Supprimer"
                    />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      <ToastContainer />
    </div>
  );
};

export default DesinscriptionsPage;